import edgeIcon from "@/assets/integrations/edge.svg";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  detectBrowser,
  useChromeExtensionStatus,
} from "@/hooks/useChromeExtensionStatus";
import { useTranslation } from "@/hooks/useTranslation";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function IntegrationEdgeExtensionPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isInstalled, isChecking } = useChromeExtensionStatus();
  const currentBrowser = detectBrowser();
  const isEdgeBrowser = currentBrowser === "edge";

  const chromeStoreUrl = import.meta.env.VITE_CHROME_STORE_URL;

  const handleOpenChromeStore = () => {
    window.open(chromeStoreUrl, "_blank");
  };

  const handleOpenReviews = () => {
    window.open(`${chromeStoreUrl}/reviews`, "_blank");
  };

  const getStatusBadge = () => {
    if (!isEdgeBrowser) {
      return null;
    }

    if (isChecking) {
      return (
        <Badge
          variant="outline"
          className="text-xs text-gray-600 border-gray-300"
        >
          {t("common.loading")}
        </Badge>
      );
    }

    if (isInstalled) {
      return (
        <Badge variant="success" className="text-xs">
          {t("team.integrations.chromeExtension.connected")}
        </Badge>
      );
    }

    return (
      <Badge variant="outline" className="text-xs">
        {t("team.integrations.chromeExtension.notConnected")}
      </Badge>
    );
  };

  return (
    <div className="mt-8">
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/integrations")}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("common.back")}
        </Button>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-4 space-y-0">
          <div className="flex items-center gap-3">
            <img src={edgeIcon} alt="Edge Extension" className="w-6 h-6" />
            <CardTitle>{t("team.integrations.edgeExtension.title")}</CardTitle>
            {getStatusBadge()}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">
            {t("team.integrations.edgeExtension.description")}
          </p>

          <div className="flex items-center gap-3">
            {isEdgeBrowser ? (
              <>
                <Button
                  onClick={handleOpenChromeStore}
                  className="flex items-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  {isInstalled
                    ? t("team.integrations.chromeExtension.visitStoreButton")
                    : t("team.integrations.chromeExtension.installButton")}
                </Button>
                {isInstalled && (
                  <Button
                    onClick={handleOpenReviews}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    {t("team.integrations.chromeExtension.reviewButton")}
                  </Button>
                )}
              </>
            ) : (
              <Button
                onClick={handleOpenChromeStore}
                className="flex items-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                {t("team.integrations.chromeExtension.visitStoreButton")}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
